import axios from "axios";
import { createStore } from "zustand/vanilla";
import { useEffect, useState } from "react";
import { getCookie } from "../../../../../utils/cookieUtils";
import { errorNoti } from "../../../../../base/BasicNotifications";
import {
  ACCESS_TOKEN_COOKIE_KEY,
  ADMIN_ACCESS_TOKEN_COOKIE_KEY,
  ADMINS_COACH_ID_COOKIE_KEY,
} from "../../../../../data/enums/TokenEnums";
import { strategyCanvasReviewUrls, REVIEW_STATUS } from "./StrategyCanvasReviewUrls";

const nodeInstance = axios.create({
  baseURL: process.env.REACT_APP_NEST_BASE_URL,
});

// The PUT/POST/DELETE endpoints (5.3/5.4/5.5) only return the single
// row/comment that changed - not fresh aggregate counts - so the FE
// recomputes reviewed_count/accepted_count/to_discuss_count/comment_count
// locally after every mutation, straight from the cached layers/blocks.
const recomputeCounts = (review) => {
  let acceptedCount = 0;
  let toDiscussCount = 0;
  let commentCount = (review.overall_comments || []).length;

  (review.layers || []).forEach((layer) => {
    commentCount += (layer.section_comments || []).length;
    (layer.blocks || []).forEach((block) => {
      if (block.review_status === REVIEW_STATUS.ACCEPTED) acceptedCount += 1;
      else if (block.review_status === REVIEW_STATUS.TO_DISCUSS) toDiscussCount += 1;
      commentCount += (block.comments || []).length;
    });
  });

  return {
    accepted_count: acceptedCount,
    to_discuss_count: toDiscussCount,
    reviewed_count: acceptedCount + toDiscussCount,
    comment_count: commentCount,
  };
};

export const useStrategyCanvasReviewStore = createStore((set, get) => ({
  reviewByUniqueId: {},
  reviewLoading: false,
  canvasModeByUniqueId: {},
  canvasModeLoading: false,
  submittingReview: false,
  liveReviewStatusByUniqueId: {},
  liveReviewStatusLoading: false,
  startingLiveReview: false,
  liveReviewByUniqueId: {},
  liveReviewLoading: false,
  editingLiveReviewBlockKey: null,
  discussingRemarkKey: null,
  lockingCanvas: false,

  _resolveAuthHeader: () => {
    const accessToken = getCookie(ACCESS_TOKEN_COOKIE_KEY);
    const adminAccessToken = getCookie(ADMIN_ACCESS_TOKEN_COOKIE_KEY);
    const adminsCoachId = getCookie(ADMINS_COACH_ID_COOKIE_KEY);
    if (accessToken) return `Bearer ${accessToken}`;
    if (adminAccessToken && adminsCoachId) return `Bearer ${adminAccessToken}`;
    return null;
  },

  _headers: () => {
    const authHeader = get()._resolveAuthHeader();
    return authHeader ? { Authorization: authHeader } : {};
  },

  // Section 5.2 - the single call for the whole Review Mode screen.
  fetchReview: async (uniqueId, { force = false } = {}) => {
    if (!uniqueId || (get().reviewByUniqueId[uniqueId] && !force)) return;
    set({ reviewLoading: true });
    try {
      const response = await nodeInstance.get(
        strategyCanvasReviewUrls.GET_REVIEW.replace("{uniqueId}", uniqueId),
        { headers: get()._headers() },
      );
      set((state) => ({
        reviewByUniqueId: {
          ...state.reviewByUniqueId,
          [uniqueId]: response.data?.data || null,
        },
        reviewLoading: false,
      }));
    } catch (error) {
      errorNoti(error.response?.data?.message || "Couldn't load the review");
      set((state) => ({
        reviewByUniqueId: { ...state.reviewByUniqueId, [uniqueId]: null },
        reviewLoading: false,
      }));
    }
  },

  // Section 6 - Canvas Mode. Fetched and cached here, rendered by
  // StrategyCanvasCanvasMode.js's CanvasModeBody/buildFormData off the real
  // API data.
  fetchCanvasMode: async (uniqueId, { force = false } = {}) => {
    if (!uniqueId || (get().canvasModeByUniqueId[uniqueId] && !force)) return;
    set({ canvasModeLoading: true });
    try {
      const response = await nodeInstance.get(
        strategyCanvasReviewUrls.GET_CANVAS_MODE.replace("{uniqueId}", uniqueId),
        { headers: get()._headers() },
      );
      set((state) => ({
        canvasModeByUniqueId: {
          ...state.canvasModeByUniqueId,
          [uniqueId]: response.data?.data || null,
        },
        canvasModeLoading: false,
      }));
    } catch (error) {
      errorNoti(error.response?.data?.message || "Couldn't load canvas mode");
      set((state) => ({
        canvasModeByUniqueId: { ...state.canvasModeByUniqueId, [uniqueId]: null },
        canvasModeLoading: false,
      }));
    }
  },

  // Section 5.3 - Accept / To-discuss on a single block. Overwrites any
  // previous decision; there is no "unset back to pending" endpoint.
  // Applied optimistically - the accordion row, its StatusPill, the Review
  // Summary panel, and the Complete-review gating all read this same cached
  // review, so flipping it here before the PUT resolves is what makes the
  // row feel instant instead of waiting on the round-trip. Rolls back to
  // whatever the decision was before this click if the save actually fails.
  setBlockStatus: async (uniqueId, blockKey, status) => {
    const review = get().reviewByUniqueId[uniqueId];
    if (!review) return false;

    const applyStatus = (targetReview, nextStatus, nextReviewedAt) => {
      const updated = {
        ...targetReview,
        layers: targetReview.layers.map((layer) => ({
          ...layer,
          blocks: layer.blocks.map((block) =>
            block.block_key === blockKey
              ? { ...block, review_status: nextStatus, reviewed_at: nextReviewedAt }
              : block,
          ),
        })),
      };
      return { ...updated, ...recomputeCounts(updated) };
    };

    const previousBlock = review.layers
      .flatMap((layer) => layer.blocks)
      .find((block) => block.block_key === blockKey);
    const previousStatus = previousBlock?.review_status ?? null;
    const previousReviewedAt = previousBlock?.reviewed_at ?? null;

    set((state) => ({
      reviewByUniqueId: {
        ...state.reviewByUniqueId,
        [uniqueId]: applyStatus(
          state.reviewByUniqueId[uniqueId] || review,
          status,
          new Date().toISOString(),
        ),
      },
    }));

    try {
      const response = await nodeInstance.put(
        strategyCanvasReviewUrls.SET_BLOCK_STATUS.replace("{uniqueId}", uniqueId).replace(
          "{blockKey}",
          blockKey,
        ),
        { status },
        { headers: get()._headers() },
      );
      const saved = response.data?.data || { status, reviewed_at: null };
      set((state) => {
        const current = state.reviewByUniqueId[uniqueId];
        if (!current) return state;
        return {
          reviewByUniqueId: {
            ...state.reviewByUniqueId,
            [uniqueId]: applyStatus(current, saved.status, saved.reviewed_at),
          },
        };
      });
      return true;
    } catch (error) {
      errorNoti(error.response?.data?.message || "Couldn't save the review decision");
      set((state) => {
        const current = state.reviewByUniqueId[uniqueId];
        if (!current) return state;
        return {
          reviewByUniqueId: {
            ...state.reviewByUniqueId,
            [uniqueId]: applyStatus(current, previousStatus, previousReviewedAt),
          },
        };
      });
      return false;
    }
  },

  // Section 5.4 - add a block / section / overall comment. Newest comment
  // is kept at the front of its array (comments[0] = latest), matching the
  // doc's guidance to render comments[0] when only showing one.
  addComment: async (uniqueId, { scopeType, scopeKey, commentText }) => {
    try {
      const response = await nodeInstance.post(
        strategyCanvasReviewUrls.ADD_COMMENT.replace("{uniqueId}", uniqueId),
        { scope_type: scopeType, scope_key: scopeKey ?? null, comment_text: commentText },
        { headers: get()._headers() },
      );
      const comment = response.data?.data;
      if (!comment) return null;

      set((state) => {
        const review = state.reviewByUniqueId[uniqueId];
        if (!review) return state;

        let updated = review;
        if (scopeType === "block") {
          updated = {
            ...review,
            layers: review.layers.map((layer) => ({
              ...layer,
              blocks: layer.blocks.map((block) =>
                block.block_key === scopeKey
                  ? { ...block, comments: [comment, ...(block.comments || [])] }
                  : block,
              ),
            })),
          };
        } else if (scopeType === "section") {
          updated = {
            ...review,
            layers: review.layers.map((layer) =>
              layer.layer === scopeKey
                ? { ...layer, section_comments: [comment, ...(layer.section_comments || [])] }
                : layer,
            ),
          };
        } else {
          updated = {
            ...review,
            overall_comments: [comment, ...(review.overall_comments || [])],
          };
        }

        return {
          reviewByUniqueId: {
            ...state.reviewByUniqueId,
            [uniqueId]: { ...updated, ...recomputeCounts(updated) },
          },
        };
      });
      return comment;
    } catch (error) {
      errorNoti(error.response?.data?.message || "Couldn't save the comment");
      return null;
    }
  },

  // Section 5.5 - delete one of the coach's own comments. The endpoint only
  // takes the comment id, so on success we just scan block/section/overall
  // comment arrays and drop whichever one matches.
  deleteComment: async (uniqueId, commentId) => {
    try {
      await nodeInstance.delete(
        strategyCanvasReviewUrls.DELETE_COMMENT.replace("{uniqueId}", uniqueId).replace(
          "{commentId}",
          commentId,
        ),
        { headers: get()._headers() },
      );
      set((state) => {
        const review = state.reviewByUniqueId[uniqueId];
        if (!review) return state;
        const dropById = (comments = []) =>
          comments.filter((comment) => comment.coach_comment_id !== commentId);
        const updated = {
          ...review,
          overall_comments: dropById(review.overall_comments),
          layers: review.layers.map((layer) => ({
            ...layer,
            section_comments: dropById(layer.section_comments),
            blocks: layer.blocks.map((block) => ({
              ...block,
              comments: dropById(block.comments),
            })),
          })),
        };
        return {
          reviewByUniqueId: {
            ...state.reviewByUniqueId,
            [uniqueId]: { ...updated, ...recomputeCounts(updated) },
          },
        };
      });
      return true;
    } catch (error) {
      errorNoti(error.response?.data?.message || "Couldn't delete the comment");
      return false;
    }
  },


  // Edit one of the coach's own comments (PUT .../review/comments/:commentId,
  // body { comment_text }). Mirrors deleteComment's approach: the endpoint
  // only takes the comment id (not its scope), so on success we scan
  // overall/section/block comment arrays and patch whichever one matches.
  // Unlike add/delete, editing text alone never changes reviewed/accepted/
  // to_discuss/comment counts, so recomputeCounts is skipped here.
  editComment: async (uniqueId, commentId, commentText) => {
    try {
      const response = await nodeInstance.put(
        strategyCanvasReviewUrls.UPDATE_COMMENT.replace("{uniqueId}", uniqueId).replace(
          "{commentId}",
          commentId,
        ),
        { comment_text: commentText },
        { headers: get()._headers() },
      );
      const saved = response.data?.data || { comment_text: commentText };
      set((state) => {
        const review = state.reviewByUniqueId[uniqueId];
        if (!review) return state;
        const applyEdit = (comments = []) =>
          comments.map((comment) =>
            comment.coach_comment_id === commentId ? { ...comment, ...saved } : comment,
          );
        const updated = {
          ...review,
          overall_comments: applyEdit(review.overall_comments),
          layers: review.layers.map((layer) => ({
            ...layer,
            section_comments: applyEdit(layer.section_comments),
            blocks: layer.blocks.map((block) => ({
              ...block,
              comments: applyEdit(block.comments),
            })),
          })),
        };
        return {
          reviewByUniqueId: { ...state.reviewByUniqueId, [uniqueId]: updated },
        };
      });
      return true;
    } catch (error) {
      errorNoti(error.response?.data?.message || "Couldn't update the comment");
      return false;
    }
  },

  // Section 7.1 - "Send to CEO" in the Canvas Review Complete modal. Only
  // succeeds once every block has a review decision - a 409 here means some
  // blocks are still unreviewed, surfaced via the backend's own message.
  submitReview: async (uniqueId, body = {}) => {
    if (!uniqueId || get().submittingReview) return null;
    set({ submittingReview: true });
    try {
      const response = await nodeInstance.post(
        strategyCanvasReviewUrls.SUBMIT_REVIEW.replace("{uniqueId}", uniqueId),
        body,
        { headers: get()._headers() },
      );
      set({ submittingReview: false });
      return response.data?.data || null;
    } catch (error) {
      errorNoti(error.response?.data?.message || "Couldn't send the review to the CEO");
      set({ submittingReview: false });
      return null;
    }
  },

  // Section 7.2 - static "waiting for live review" screen, landed on right
  // after 7.1 succeeds.
  fetchLiveReviewStatus: async (uniqueId, { force = false } = {}) => {
    if (!uniqueId || (get().liveReviewStatusByUniqueId[uniqueId] && !force)) return;
    set({ liveReviewStatusLoading: true });
    try {
      const response = await nodeInstance.get(
        strategyCanvasReviewUrls.GET_LIVE_REVIEW_STATUS.replace("{uniqueId}", uniqueId),
        { headers: get()._headers() },
      );
      set((state) => ({
        liveReviewStatusByUniqueId: {
          ...state.liveReviewStatusByUniqueId,
          [uniqueId]: response.data?.data || null,
        },
        liveReviewStatusLoading: false,
      }));
    } catch (error) {
      errorNoti(error.response?.data?.message || "Couldn't load the live review status");
      set((state) => ({
        liveReviewStatusByUniqueId: { ...state.liveReviewStatusByUniqueId, [uniqueId]: null },
        liveReviewStatusLoading: false,
      }));
    }
  },

  // Section 7.3 - Start Live Review. Only valid while canvas_state is
  // COACH_REVIEW_SUBMITTED; flips it to LIVE_REVIEW_IN_PROGRESS.
  startLiveReview: async (uniqueId) => {
    if (!uniqueId || get().startingLiveReview) return null;
    set({ startingLiveReview: true });
    try {
      const response = await nodeInstance.post(
        strategyCanvasReviewUrls.START_LIVE_REVIEW.replace("{uniqueId}", uniqueId),
        {},
        { headers: get()._headers() },
      );
      const data = response.data?.data || null;
      set((state) => ({
        startingLiveReview: false,
        liveReviewStatusByUniqueId: data
          ? {
              ...state.liveReviewStatusByUniqueId,
              [uniqueId]: { ...state.liveReviewStatusByUniqueId[uniqueId], ...data },
            }
          : state.liveReviewStatusByUniqueId,
      }));
      return data;
    } catch (error) {
      errorNoti(error.response?.data?.message || "Couldn't start the live review");
      set({ startingLiveReview: false });
      return null;
    }
  },

  // Section 7.4 - the single call for the whole Live Review session screen:
  // all blocks (editable, read-only here) grouped by layer, plus the
  // deduped "Your Remarks" list. Only ever returns undiscussed remarks.
  fetchLiveReview: async (uniqueId, { force = false } = {}) => {
    if (!uniqueId || (get().liveReviewByUniqueId[uniqueId] && !force)) return;
    set({ liveReviewLoading: true });
    try {
      const response = await nodeInstance.get(
        strategyCanvasReviewUrls.GET_LIVE_REVIEW.replace("{uniqueId}", uniqueId),
        { headers: get()._headers() },
      );
      set((state) => ({
        liveReviewByUniqueId: {
          ...state.liveReviewByUniqueId,
          [uniqueId]: response.data?.data || null,
        },
        liveReviewLoading: false,
      }));
    } catch (error) {
      errorNoti(error.response?.data?.message || "Couldn't load the live review");
      set((state) => ({
        liveReviewByUniqueId: { ...state.liveReviewByUniqueId, [uniqueId]: null },
        liveReviewLoading: false,
      }));
    }
  },

  // Section 7.5 - directly overwrite a block's value during Live Review. No
  // confirmation/proposal step - whatever is sent becomes the value.
  editLiveReviewBlock: async (uniqueId, blockKey, value) => {
    if (!uniqueId || !blockKey || get().editingLiveReviewBlockKey) return false;
    set({ editingLiveReviewBlockKey: blockKey });
    try {
      const response = await nodeInstance.put(
        strategyCanvasReviewUrls.EDIT_LIVE_REVIEW_BLOCK.replace("{uniqueId}", uniqueId).replace(
          "{blockKey}",
          blockKey,
        ),
        { value },
        { headers: get()._headers() },
      );
      const saved = response.data?.data || { block_key: blockKey, value };
      set((state) => {
        const liveReview = state.liveReviewByUniqueId[uniqueId];
        // The Canvas Mode tab renders from its own separately-cached
        // canvasModeByUniqueId snapshot, so an edit made here in the
        // accordion wouldn't otherwise show up over there. Drop the cached
        // entry so StrategyCanvasCanvasMode.js's own fetchCanvasMode call
        // (which only fetches when nothing is cached) re-fetches fresh data
        // the next time the coach switches into Canvas Mode.
        const { [uniqueId]: _dropped, ...remainingCanvasMode } = state.canvasModeByUniqueId;
        if (!liveReview) {
          return { editingLiveReviewBlockKey: null, canvasModeByUniqueId: remainingCanvasMode };
        }
        return {
          editingLiveReviewBlockKey: null,
          canvasModeByUniqueId: remainingCanvasMode,
          liveReviewByUniqueId: {
            ...state.liveReviewByUniqueId,
            [uniqueId]: {
              ...liveReview,
              layers: liveReview.layers.map((layer) => ({
                ...layer,
                blocks: layer.blocks.map((block) =>
                  block.block_key === saved.block_key ? { ...block, value: saved.value } : block,
                ),
              })),
            },
          },
        };
      });
      return true;
    } catch (error) {
      errorNoti(error.response?.data?.message || "Couldn't save the block");
      set({ editingLiveReviewBlockKey: null });
      return false;
    }
  },

  // Accept a block during the Live Review session (Figma node
  // 33450-155934) - reuses the same 5.3 set-status endpoint the solo Review
  // Mode screen uses, since it's the same underlying block record; only the
  // liveReviewByUniqueId cache is updated here (no "to discuss" option is
  // shown once a block already reached Live Review, only Accept/Edit).
  settingLiveReviewBlockStatus: null,
  setLiveReviewBlockStatus: async (uniqueId, blockKey, status) => {
    if (!uniqueId || !blockKey || get().settingLiveReviewBlockStatus) return false;
    set({ settingLiveReviewBlockStatus: blockKey });
    try {
      const response = await nodeInstance.put(
        strategyCanvasReviewUrls.SET_BLOCK_STATUS.replace("{uniqueId}", uniqueId).replace(
          "{blockKey}",
          blockKey,
        ),
        { status },
        { headers: get()._headers() },
      );
      const saved = response.data?.data || { status };
      set((state) => {
        const liveReview = state.liveReviewByUniqueId[uniqueId];
        if (!liveReview) return { settingLiveReviewBlockStatus: null };
        return {
          settingLiveReviewBlockStatus: null,
          liveReviewByUniqueId: {
            ...state.liveReviewByUniqueId,
            [uniqueId]: {
              ...liveReview,
              layers: liveReview.layers.map((layer) => ({
                ...layer,
                blocks: layer.blocks.map((block) =>
                  block.block_key === blockKey
                    ? { ...block, status: saved.status }
                    : block,
                ),
              })),
            },
          },
        };
      });
      return true;
    } catch (error) {
      errorNoti(error.response?.data?.message || "Couldn't save the review decision");
      set({ settingLiveReviewBlockStatus: null });
      return false;
    }
  },

  // Section 7.6 - mark a remark as Discussed. One-directional - there's no
  // "un-discuss". Real 7.4 remark shape (confirmed against a live payload):
  // { remark_type: "comment" | "block", id, block_key, block_label,
  // comment_text, discussed, discussed_at }. The remark stays in the list
  // on success (its card just flips from "To discuss" to "Discussed") with
  // discussed: true set client-side, and remarks_remaining/discussed_count
  // adjusted to match rather than forcing a full refetch.
  markRemarkDiscussed: async (uniqueId, remarkType, id) => {
    const key = `${remarkType}-${id}`;
    if (!uniqueId || get().discussingRemarkKey) return false;
    set({ discussingRemarkKey: key });
    try {
      await nodeInstance.put(
        strategyCanvasReviewUrls.MARK_REMARK_DISCUSSED.replace("{uniqueId}", uniqueId),
        { remark_type: remarkType, id },
        { headers: get()._headers() },
      );
      set((state) => {
        const liveReview = state.liveReviewByUniqueId[uniqueId];
        if (!liveReview) return { discussingRemarkKey: null };
        const discussedAt = new Date().toISOString();
        const updatedRemarks = (liveReview.remarks || []).map((remark) =>
          remark.remark_type === remarkType && remark.id === id
            ? { ...remark, discussed: true, discussed_at: discussedAt }
            : remark,
        );
        const remainingCount = updatedRemarks.filter((remark) => !remark.discussed).length;
        return {
          discussingRemarkKey: null,
          liveReviewByUniqueId: {
            ...state.liveReviewByUniqueId,
            [uniqueId]: {
              ...liveReview,
              remarks: updatedRemarks,
              remarks_remaining: remainingCount,
              discussed_count: updatedRemarks.length - remainingCount,
            },
          },
        };
      });
      return true;
    } catch (error) {
      errorNoti(error.response?.data?.message || "Couldn't mark the remark as discussed");
      set({ discussingRemarkKey: null });
      return false;
    }
  },

  // Section 7.7 - lock the canvas. Only succeeds once remarks_remaining is
  // 0; a 409 here means remarks are still outstanding.
  lockCanvas: async (uniqueId) => {
    if (!uniqueId || get().lockingCanvas) return null;
    set({ lockingCanvas: true });
    try {
      const response = await nodeInstance.post(
        strategyCanvasReviewUrls.LOCK_LIVE_REVIEW.replace("{uniqueId}", uniqueId),
        {},
        { headers: get()._headers() },
      );
      const data = response.data?.data || null;
      set((state) => ({
        lockingCanvas: false,
        liveReviewStatusByUniqueId: data
          ? {
              ...state.liveReviewStatusByUniqueId,
              [uniqueId]: { ...state.liveReviewStatusByUniqueId[uniqueId], ...data },
            }
          : state.liveReviewStatusByUniqueId,
      }));
      return data;
    } catch (error) {
      errorNoti(error.response?.data?.message || "Couldn't lock the strategy canvas");
      set({ lockingCanvas: false });
      return null;
    }
  },
}));

export default useStrategyCanvasReviewStore;

export const useStrategyCanvasReviewStoreSelector = (selector) => {
  const [selected, setSelected] = useState(() =>
    selector(useStrategyCanvasReviewStore.getState()),
  );
  useEffect(() => {
    const unsub = useStrategyCanvasReviewStore.subscribe((state) => {
      setSelected(() => selector(state));
    });
    return () => unsub();
  }, [selector]);
  return selected;
};
